import React, { useState, useEffect } from 'react';
import GlobalNav from '../components/GlobalNav';
import Footer from '../components/Footer';
import './Epk.css';

export default function Epk({ embedded = false }) {
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);

  useEffect(() => {
    async function fetchArtistData() {
      const path = window.location.pathname;
      const userData = localStorage.getItem('user');
      const loggedInUser = userData ? JSON.parse(userData) : null;

      if (path.startsWith('/epk/')) {
        const identifier = path.split('/epk/')[1];
        try {
          const res = await fetch(`/api/artist/${identifier}`);
          const data = await res.json();
          if (data.success) {
            setArtist(data.artist);
            const isSame = loggedInUser && (
              data.artist.id === loggedInUser.id ||
              data.artist.slug === loggedInUser.slug ||
              data.artist.username === loggedInUser.username
            );
            setIsOwnProfile(!!isSame);
          }
        } catch (e) {
          console.error('Error fetching artist:', e);
        }
      } else if (loggedInUser) {
        // /profile (no identifier in URL) — don't just use the cached
        // session object, it's deliberately trimmed (see routes/users.js
        // login route) and lacks bio/official/video/audio/roles. Fetch the
        // full record the same way /epk/:identifier does.
        const identifier = loggedInUser.slug || loggedInUser.username || loggedInUser.id;
        try {
          const res = await fetch(`/api/artist/${identifier}`);
          const data = await res.json();
          if (data.success) {
            setArtist(data.artist);
          } else {
            setArtist(loggedInUser); // fallback if lookup fails
          }
        } catch (e) {
          console.error('Error fetching own profile:', e);
          setArtist(loggedInUser);
        }
        setIsOwnProfile(true);
      }
      setLoading(false);
    }
    fetchArtistData();
  }, []);

  if (loading) {
    return (
      <div className="epk-page">
        {!embedded && <GlobalNav activeHref="" />}
        <div className="epk-loading">
          <div className="epk-spinner" />
          <p>Loading profile…</p>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="epk-page">
        {!embedded && <GlobalNav activeHref="" />}
        <div className="epk-notfound">
          <h2>Artist profile not found.</h2>
          <a href="/registry" className="mihu-btn-secondary-lg">Browse the registry</a>
        </div>
      </div>
    );
  }

  const displayName = artist.artistname || `${artist.firstname || ''} ${artist.lastname || ''}`.trim();

  return (
    <div className="epk-page">
      {!embedded && <GlobalNav activeHref="" />}

      <div className="epk-hero" style={{ backgroundImage: "url('/images/mtshasta-crop.jpg')" }}>
        <div className="epk-hero-inner">
          <div className="epk-avatar">
            <img src={artist.photo || '/images/default-profile.png'} alt={displayName} />
          </div>
          <div className="epk-hero-info">
            <h1 className="epk-name">{displayName}</h1>
            {(artist.roles || artist.role) && (
              <div className="epk-role">{artist.roles || artist.role}</div>
            )}
            <div className="epk-hero-ctas">
              {artist.email && (
                <a className="mihu-btn-primary-lg" href={`mailto:${artist.email}`}>
                  Contact
                </a>
              )}
              {isOwnProfile && artist.secrettoken && (
                <a className="mihu-btn-secondary-lg" href={`/epk-update/${artist.secrettoken}`}>
                  Edit profile
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="epk-body">
        <div className="epk-main">

          {artist.bio && (
            <section className="epk-section">
              <div className="epk-section-label">Bio</div>
              <p className="epk-bio">{artist.bio}</p>
            </section>
          )}

          {artist.official && (
            <section className="epk-section">
              <div className="epk-section-label">Website</div>
              <a className="epk-link" href={artist.official} target="_blank" rel="noopener noreferrer">
                {artist.official}
              </a>
            </section>
          )}

          {artist.video && (
            <section className="epk-section">
              <div className="epk-section-label">Video</div>
              <div className="epk-video">
                <iframe
                  src={artist.video}
                  title="Artist video"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </section>
          )}
        </div>

        <div className="epk-side">
          {[1, 2, 3].map((n) => (
            artist[`audio${n}`] && (
              <div key={n} className="epk-audio-track">
                <div className="epk-audio-title">
                  {artist[`audio${n}title`] || artist[`audio${n}Title`] || `Track ${n}`}
                </div>
                <audio controls>
                  <source src={artist[`audio${n}`]} type="audio/mpeg" />
                </audio>
              </div>
            )
          ))}
        </div>
      </div>

      {!embedded && <Footer />}
    </div>
  );
}
